/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Checkbox,
  Chip,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme
} from '@mui/material';
import { IObject } from 'src/models/objectManagement';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useEffect, useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import { useLocation } from 'react-router-dom';
import { useAlert } from 'src/hooks/useAlert';
import { TOTAL_SOCIAL_MEDIA } from 'src/models/general';
import { Google } from '@mui/icons-material';
import useDebounceCallback from 'src/hooks/useDebounceCallback';

import GetSocialMediaIcon from 'src/components/GetSocialMediaIcon';
import { useTranslation } from 'react-i18next';
import { projectIds } from 'src/constants/projectIds';

interface Props {
  objectList: Array<IObject>;
  visibleAmount?: number;
}

type BoxProps = {
  backgroundColor?: string;
};

const Container = styled(Box)<BoxProps>(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 11px 8px 11px;
    box-sizing: border-box;
  `
);

const CheckboxCustom = styled(Checkbox)(
  ({ theme }) => `
    &:hover {
      background-color: unset;
    }
    padding: 0
  `
);

interface ColorChip {
  colorChip: string;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11
  }
}));

const ChipCustom = styled(Chip)<ColorChip>(
  ({ theme, colorChip }) => `
    margin-right: ${theme.spacing(1)};
    background-color: ${theme.palette.primary.main};
    border-radius: ${theme.spacing(1.43)};
    padding: ${theme.spacing(1)} 0px ${theme.spacing(1)} 2px;
    width: ${theme.spacing(7)};
    height: ${theme.spacing(3)};
    cursor: pointer;
    &:hover {
      background-color: ${theme.palette.primary.dark};
    }
    .MuiChip-label {
        color: ${colorChip};
        font-size: 13px;
        line-height: 130%;
        margin: 8px 0px 8px 8px;
    }
    .MuiChip-avatarSmall {
        width: 14px;
        color: ${colorChip};
        margin: 0px -3px 0px 0px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: ${colorChip};
    }
  `
);

// disable telegram and whatsapp
const disabledIndex = [6, 7];

const ObjectPreviewRetail: React.FC<Props> = ({
  objectList,
  visibleAmount = 3
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const objectIdList = useAppSelector(
    (state) => state.analyticArguments.objectIdList
  );
  const isGoogleReviewActive = useAppSelector(
    (state) => state.users.project.flags.isGoogleReview
  );
  const project = useAppSelector((state) => state.users.project);
  const { pathname } = useLocation();
  const subtitle = pathname.split('/')[2];
  const title = pathname.split('/')[1];
  const isGoogleReviewPage = title === 'google_review';
  const isStreamPanel = title === 'stream_panel';
  const isInstagramPerformance =
    subtitle && subtitle === 'instagram_performance';
  const showRedditObject = project.id !== projectIds.bni;

  // only enable google review object
  const googleReviewInitState = [
    ...new Array(TOTAL_SOCIAL_MEDIA - 1).fill(true),
    false
  ];

  const socialMediaInitState = [
    ...new Array(TOTAL_SOCIAL_MEDIA - 1)
      .fill(false)
      .map((item, index) => disabledIndex.includes(index)),
    true
  ];

  const allInitState = [
    ...new Array(TOTAL_SOCIAL_MEDIA)
      .fill(false)
      .map((item, index) => disabledIndex.includes(index))
  ];

  const initCheckedState = isGoogleReviewPage
    ? googleReviewInitState
    : socialMediaInitState;

  // OBJECT IS ACTIVE IF THE VALUE IS FALSE!
  const [checkedState, setCheckedState] = useState(
    isStreamPanel ? allInitState : initCheckedState
  );

  const { handleClickAlert } = useAlert();
  const autoRefreshInterval = useAppSelector(
    (state) => state.autoRefreshMetric.selectedAutoRefreshInterval
  );
  const { isOnTour } = useAppSelector((state) => state.storeProductTour);

  // const objectListByObjectTypeActive = useMemo(() => {
  //   return objectList.filter((object) =>
  //     objectIdList.find((id) => id === object.id)
  //   );
  // }, [objectIdList, objectList]);

  useEffect(() => {
    if (isStreamPanel) {
      setCheckedState(allInitState);
      return;
    }
    if (isGoogleReviewPage) setCheckedState(googleReviewInitState);
    else setCheckedState(socialMediaInitState);
  }, [subtitle, objectList, isGoogleReviewPage, isStreamPanel]);

  const objectListByTwitter = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'twitter');
  }, [objectIdList, objectList]);
  const objectListByFacebook = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'facebook');
  }, [objectIdList, objectList]);
  const objectListByInstagram = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'instagram');
  }, [objectIdList, objectList]);
  const objectListByYoutube = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'youtube');
  }, [objectIdList, objectList]);
  const objectListByTiktok = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'tiktok');
  }, [objectIdList, objectList]);
  const objectListByLinkedin = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'linkedin');
  }, [objectIdList, objectList]);
  const objectListByGoogle = useMemo(() => {
    if (!isGoogleReviewActive) return [];
    return objectList.filter((object) => object.socialMedia === 'googleReview');
  }, [objectIdList, objectList, isGoogleReviewActive]);
  // const objectListByTelegram = useMemo(() => {
  //   return objectList.filter((object) => object.socialMedia === 'telegram');
  // }, [objectIdList, objectList]);
  // const objectListByWhatsApp = useMemo(() => {
  //   return objectList.filter((object) => object.socialMedia === 'whatsapp');
  // }, [objectIdList, objectList]);
  const objectListByThreads = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'threads');
  }, [objectIdList, objectList]);
  const objectListByReddit = useMemo(() => {
    return objectList.filter((object) => object.socialMedia === 'reddit');
  }, [objectIdList, objectList]);

  const getActiveObjectIds = (currentCheckedState: boolean[]): string[] => {
    let activeObjects: IObject[] = [];

    // Add objects from each unchecked (active) social media type
    if (!currentCheckedState[0])
      activeObjects = [...activeObjects, ...objectListByTwitter];
    if (!currentCheckedState[1])
      activeObjects = [...activeObjects, ...objectListByFacebook];
    if (!currentCheckedState[2])
      activeObjects = [...activeObjects, ...objectListByInstagram];
    if (!currentCheckedState[3])
      activeObjects = [...activeObjects, ...objectListByYoutube];
    if (!currentCheckedState[4])
      activeObjects = [...activeObjects, ...objectListByTiktok];
    if (!currentCheckedState[5])
      activeObjects = [...activeObjects, ...objectListByLinkedin];
    // if (!currentCheckedState[6])
    //   activeObjects = [...activeObjects, ...objectListByTelegram];
    // if (!currentCheckedState[7])
    //   activeObjects = [...activeObjects, ...objectListByWhatsApp];
    if (!currentCheckedState[8])
      activeObjects = [...activeObjects, ...objectListByThreads];
    if (!currentCheckedState[9])
      activeObjects = [...activeObjects, ...objectListByReddit];
    if (!currentCheckedState[10])
      activeObjects = [...activeObjects, ...objectListByGoogle];

    return activeObjects.map((obj) => obj.id);
  };

  const debouncedUpdateObjectList = useDebounceCallback(
    (updatedCheckedState: boolean[]) => {
      const activeObjectIds = getActiveObjectIds(updatedCheckedState);
      dispatch(reducerUpdateAnalyticArgumentObjectIdList(activeObjectIds));
    },
    1000
  );

  const handleClickAll = () => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }
    const dataList = objectList.map((object) => {
      return object.id;
    });
    // checkedState.map((item, index) =>
    //   setCheckedState(new Array(TOTAL_SOCIAL_MEDIA).fill(false))
    // );
    setCheckedState(isStreamPanel ? allInitState : initCheckedState);
    dispatch(reducerUpdateAnalyticArgumentObjectIdList(dataList));
  };

  const handleClickSource = (position: number) => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }

    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item
    );
    setCheckedState(updatedCheckedState);
    debouncedUpdateObjectList(updatedCheckedState);
  };

  return (
    <Container
      flexDirection="row"
      flexWrap={'wrap'}
      alignItems={{ xs: 'left', md: 'center' }}
      justifyContent={{ xs: 'left' }}
      sx={isOnTour ? { pointerEvents: 'none' } : {}}
      gap={0.5}
    >
      {!isInstagramPerformance ? (
        <Button
          onClick={handleClickAll}
          sx={{
            marginRight: theme.spacing(1),
            background: '#E0F6FF',
            borderRadius: '16px'
          }}
        >
          <LightTooltip
            title={objectList
              .map((object) => {
                return object.objectName;
              })
              .join(', ')}
          >
            <Typography
              sx={{ fontSize: '14px', color: '#009FE3', fontWeight: 600 }}
            >
              {t('all')}
            </Typography>
          </LightTooltip>
        </Button>
      ) : null}

      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(0)}
          checked={checkedState[0]}
          disabled={
            objectListByTwitter.length > 0 && !isGoogleReviewPage ? false : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'twitter'}
                  sx={{ color: '#FBFBFB', fontSize: 12 }}
                />
              }
              label={objectListByTwitter.length}
              size="small"
              sx={{
                background: `${
                  objectListByTwitter.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? '#000000'
                    : '#DBDBDB'
                }`
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'twitter'}
                  sx={{ color: '#FBFBFB', fontSize: 12 }}
                />
              }
              label={objectListByTwitter.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}

      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(1)}
          checked={checkedState[1]}
          disabled={
            objectListByFacebook.length > 0 && !isGoogleReviewPage
              ? false
              : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'facebook'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByFacebook.length}
              size="small"
              sx={{
                background: `${
                  objectListByFacebook.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? '#3B5998'
                    : '#DBDBDB'
                }`
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'facebook'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByFacebook.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}

      <CheckboxCustom
        onClick={() => handleClickSource(2)}
        checked={checkedState[2]}
        disabled={
          objectListByInstagram.length > 0 && !isGoogleReviewPage ? false : true
        }
        icon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'instagram'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByInstagram.length}
            size="small"
            sx={{
              background: `${
                objectListByInstagram.length > 0 && !isGoogleReviewPage
                  ? '#7A3EAE'
                  : '#DBDBDB'
              }`
            }}
            colorChip={theme.palette.common.white}
          />
        }
        checkedIcon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'instagram'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByInstagram.length}
            size="small"
            sx={{
              background: '#DBDBDB'
            }}
            colorChip={theme.palette.common.white}
          />
        }
      />

      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(3)}
          checked={checkedState[3]}
          disabled={
            objectListByYoutube.length > 0 && !isGoogleReviewPage ? false : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'youtube'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByYoutube.length}
              size="small"
              sx={{
                background: `${
                  objectListByYoutube.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? '#FF0000'
                    : '#DBDBDB'
                }`
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'youtube'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByYoutube.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(4)}
          checked={checkedState[4]}
          disabled={
            objectListByTiktok.length > 0 && !isGoogleReviewPage ? false : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'tiktok'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByTiktok.length}
              size="small"
              sx={{
                background: `${
                  objectListByTiktok.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? themeColors.tiktok
                    : '#DBDBDB'
                }`
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'tiktok'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByTiktok.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(5)}
          checked={checkedState[5]}
          disabled={
            objectListByLinkedin.length > 0 && !isGoogleReviewPage
              ? false
              : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'linkedin'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByLinkedin.length}
              size="small"
              sx={{
                background:
                  objectListByLinkedin.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? themeColors.linkedin
                    : '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'linkedin'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByLinkedin.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
      {/*
      <CheckboxCustom
        onClick={() => handleClickSource(6)}
        checked={checkedState[6]}
        disabled={objectListByTelegram.length > 0 ? false : true}
        icon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'telegram'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByTelegram.length}
            size="small"
            sx={{
              background:
                objectListByTelegram.length > 0
                  ? themeColors.telegram
                  : '#DBDBDB'
            }}
            colorChip={theme.palette.common.white}
          />
        }
        checkedIcon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'telegram'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByTelegram.length}
            size="small"
            sx={{
              background: '#DBDBDB'
            }}
            colorChip={theme.palette.common.white}
          />
        }
      />
      <CheckboxCustom
        onClick={() => handleClickSource(7)}
        checked={checkedState[7]}
        disabled={objectListByWhatsApp.length > 0 ? false : true}
        icon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'whatsapp'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByWhatsApp.length}
            size="small"
            sx={{
              background:
                objectListByWhatsApp.length > 0
                  ? themeColors.whatsapp
                  : '#DBDBDB'
            }}
            colorChip={theme.palette.common.white}
          />
        }
        checkedIcon={
          <ChipCustom
            variant="filled"
            deleteIcon={<CheckIcon />}
            avatar={
              <GetSocialMediaIcon
                socialMedia={'whatsapp'}
                sx={{ color: '#FBFBFB', fontSize: 16 }}
              />
            }
            label={objectListByWhatsApp.length}
            size="small"
            sx={{
              background: '#DBDBDB'
            }}
            colorChip={theme.palette.common.white}
          />
        }
      />*/}
      {!isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(8)}
          checked={checkedState[8]}
          disabled={
            objectListByThreads.length > 0 && !isGoogleReviewPage ? false : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'threads'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByThreads.length}
              size="small"
              sx={{
                background:
                  objectListByThreads.length > 0 &&
                  !isGoogleReviewPage &&
                  !isInstagramPerformance
                    ? themeColors.threads
                    : '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'threads'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByThreads.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
      {showRedditObject && !isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(9)}
          checked={checkedState[9]}
          disabled={
            objectListByReddit.length > 0 && !isGoogleReviewPage ? false : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'reddit'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByReddit.length}
              size="small"
              sx={{
                background:
                  objectListByReddit.length > 0 && !isGoogleReviewPage
                    ? themeColors.reddit
                    : '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'reddit'}
                  sx={{ color: '#FBFBFB', fontSize: 16 }}
                />
              }
              label={objectListByReddit.length}
              size="small"
              sx={{
                background: '#DBDBDB'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
      {isGoogleReviewActive && !isInstagramPerformance ? (
        <CheckboxCustom
          onClick={() => handleClickSource(10)}
          checked={checkedState[10]}
          disabled={
            objectListByGoogle.length > 0 &&
            (isGoogleReviewPage || isStreamPanel)
              ? false
              : true
          }
          icon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={
                <GetSocialMediaIcon
                  socialMedia={'googleReview'}
                  sx={{ color: '#FBFBFB', fontSize: 14 }}
                />
              }
              label={objectListByGoogle.length}
              size="small"
              sx={{
                background:
                  objectListByGoogle.length > 0 &&
                  (isGoogleReviewPage || isStreamPanel)
                    ? '#fff'
                    : '#DBDBDB',
                border: '1px solid #D9D9D9'
                // color: '#4B4B4B'
              }}
              colorChip={'#4B4B4B'}
            />
          }
          checkedIcon={
            <ChipCustom
              variant="filled"
              deleteIcon={<CheckIcon />}
              avatar={<Google sx={{ color: '#fff' }} />}
              label={objectListByGoogle.length}
              size="small"
              sx={{
                background:
                  objectListByGoogle.length > 0 ? '#D9D9D9' : '#DBDBDB',
                border: '1px solid #D9D9D9',
                color: '#4B4B4B'
              }}
              colorChip={theme.palette.common.white}
            />
          }
        />
      ) : null}
    </Container>
  );
};

export default ObjectPreviewRetail;
